// react
import { useState } from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Editor } from '@/components/organisms/Editor/Editor';
import { FieldDef } from '@/components/organisms/EditorFields/fieldHelper';
import { SectionsAccordion } from '@/pwa/organisms/SectionsAccordion/SectionsAccordion';

// models
import { Section } from '@karehero/models';

// mui
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const Progress = ({ progress }: { progress: number }) => {
  return (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        alignItems: 'center',
        flexGrow: 1,
      }}
    >
      <LinearProgress
        variant='determinate'
        value={progress}
        sx={{ flexGrow: 1 }}
      />
      <Typography variant='caption'>{progress}%</Typography>
    </Stack>
  );
};

export interface CareProfileFormPageProps<T = any> {
  title: string;
  progress?: number;
  fields: { [key in keyof T]: FieldDef };
  value: T;
  onChange: (value: T, key: string) => void;
  onBack?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onPrint?: () => void;
  onEdit?: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  isDisabled?: boolean;
  isSaveDisabled?: boolean;
  isLoading?: boolean;
  sections?: Section[];
  currentPath?: string;
  onNavigateSection?: (to: string) => void;
}

/**
 * CareProfileFormPage is a care profile form page with a title, optional progress bar,
 * action icons, and an editor form.
 */
export const CareProfileFormPage = ({
  title,
  progress,
  fields,
  value,
  onChange,
  onBack,
  onSave,
  onCancel,
  onPrint,
  onEdit,
  saveLabel = 'Continue',
  cancelLabel = 'Previous',
  isDisabled,
  isSaveDisabled,
  isLoading,
  sections,
  currentPath,
  onNavigateSection,
}: CareProfileFormPageProps) => {
  const [isSectionsOpen, setIsSectionsOpen] = useState(false);
  const theme = useTheme();

  return (
    <>
      <ModalPageTemplate
        onBack={onBack}
        footerSlot={
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              spacing={2}
              sx={{
                maxWidth: 'sm',
                mx: 'auto',
              }}
            >
              <Button
                variant='contained'
                size='large'
                fullWidth
                onClick={onSave}
                disabled={isSaveDisabled || isLoading}
              >
                {saveLabel}
              </Button>
              {onCancel && (
                <Button
                  variant='outlined'
                  size='large'
                  fullWidth
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  {cancelLabel}
                </Button>
              )}
            </Stack>
          </Box>
        }
      >
        <PageTemplate
          maxWidth='sm'
          headerSlot={
            <Stack spacing={1}>
              <Stack
                direction='row'
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='h5'
                  sx={{
                    flexGrow: 1,
                  }}
                >
                  {title}
                </Typography>
                {onEdit && (
                  <IconButton onClick={onEdit}>
                    <Icon icon='edit' fontSize={20} />
                  </IconButton>
                )}
                {onPrint && (
                  <IconButton onClick={onPrint}>
                    <Icon icon='print' fontSize={20} />
                  </IconButton>
                )}
                {sections && sections.length > 0 && (
                  <IconButton onClick={() => setIsSectionsOpen(true)}>
                    <Icon icon='list' fontSize={20} />
                  </IconButton>
                )}
              </Stack>
              {progress !== undefined && <Progress progress={progress} />}
            </Stack>
          }
          contentSlot={
            <Stack spacing={4}>
              <Editor
                fields={fields}
                value={value}
                onChange={onChange}
                onSave={onSave}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isActionsDisabled
                isFloatingActions={false}
              />
            </Stack>
          }
        />
      </ModalPageTemplate>
      <SectionsAccordion
        sections={sections}
        isOpen={isSectionsOpen}
        setIsOpen={setIsSectionsOpen}
        currentPath={currentPath}
        onNavigate={onNavigateSection}
      />
    </>
  );
};

CareProfileFormPage.displayName = 'CareProfileFormPage';

export default CareProfileFormPage;
